/**
 * A firm's own identity: its name, the lines customers ring, a few words about it, its
 * colour and logo and legal links — and what its drivers' phones look like.
 *
 * The white-label promise from the blueprint, reachable without SQL: one binary serves every
 * firm, and what makes a firm look like itself is data they own.
 *
 * Three documents on one page, because an administrator looking for one will look here for
 * the others. The **profile** — name, lines, words, hours, address — is what the first
 * screens of a new firm's setup ask for and what the passenger app's footer renders. The
 * **branding** is a colour, a logo and two legal links. **Appearance** is an operational
 * choice about work phones. The support number used to be a field of its own here; it is
 * the line flagged in the list now, and the server writes it onto the branding for every
 * client that already reads it there.
 *
 * The boundary from `docs/branding.md` holds here exactly as it does on mobile. A firm
 * chooses one colour; danger, warning and confirmation are **not** brandable, because they
 * carry meaning rather than identity — and a firm whose accent happens to be red must not
 * end up with a console where a cancellation looks like an ordinary button. The hint under
 * the colour row says so, so nobody has to find out.
 *
 * **One save bar for three endpoints.** Every card is a draft against what was loaded, and
 * Save sends only the documents that differ — profile, then branding, then the driver-app
 * theme — so a refusal from one leaves the others' changes saved and the count showing
 * what is still outstanding. Apply-to-all is refused while the theme differs from what is
 * stored, since clearing every driver's own setting to a value the screen is no longer
 * showing cannot be undone.
 *
 * The logo is the exception and stays outside the draft: choosing a file uploads it and
 * Remove deletes it, both immediately, so neither Discard nor Save has anything to say about
 * them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  aboutUsMaxLength,
  brandingApi,
  profileApi,
  settingsApi,
  type BrandingSettings,
  type FirmProfile,
  type ThemePreference,
} from '../api';
import { refusal } from '../errors';
import { checkForm, named } from '../check';
import { notifySetupChanged } from '../setup/events';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  FieldErrors,
  Input,
  PageHeader,
  SaveBar,
  Section,
  Select,
  SettingRow,
  Textarea,
  type FieldProblem,
} from '../../ui';
import { PhoneLinesEditor } from './PhoneLinesEditor';
import { withoutEmptyRows } from './phoneLines';

const THEMES: readonly ThemePreference[] = ['System', 'Light', 'Dark'];

/** Blank and the null the server sent are the same setting. */
const same = (left: string | null | undefined, right: string | null | undefined) =>
  (left ?? '') === (right ?? '');

/** How many of the profile's fields differ from what was loaded. The lines count as one. */
function profileChanges(loaded: FirmProfile, draft: FirmProfile): number {
  let count = 0;

  if (loaded.displayName !== draft.displayName) count++;
  if (JSON.stringify(loaded.phoneLines) !== JSON.stringify(draft.phoneLines)) count++;
  if (!same(loaded.aboutUs, draft.aboutUs)) count++;
  if (!same(loaded.website, draft.website)) count++;
  if (!same(loaded.email, draft.email)) count++;
  if (!same(loaded.openingHours, draft.openingHours)) count++;
  if (!same(loaded.officeAddress, draft.officeAddress)) count++;

  return count;
}

export function BrandingPage() {
  const { t } = useTranslation();
  // What `checkForm` reads before a save: every control on the page, at once (#377).
  const page = useRef<HTMLDivElement>(null);

  /** What the server last told us: what Discard returns to, and what the count is read against. */
  const [loadedProfile, setLoadedProfile] = useState<FirmProfile | null>(null);
  const [profile, setProfile] = useState<FirmProfile | null>(null);
  const [loadedBranding, setLoadedBranding] = useState<BrandingSettings | null>(null);
  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  const [loadedTheme, setLoadedTheme] = useState<ThemePreference | null>(null);
  const [theme, setTheme] = useState<ThemePreference | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);

  /** What the apply-to-all action actually did, so the screen can say rather than imply. */
  const [cleared, setCleared] = useState<number | null>(null);
  const [problem, setProblem] = useState<FieldProblem | null>(null);

  /** Bumped after an upload so the browser refetches an image at an unchanged URL. */
  const [logoVersion, setLogoVersion] = useState(0);

  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const firm = await profileApi.get();

        setLoadedProfile(firm);
        setProfile(firm);
      } catch (failure) {
        setProblem(refusal(failure, t, 'branding.couldNotLoadProfile'));
      }
    })();

    void (async () => {
      try {
        const settings = await brandingApi.get();

        setLoadedBranding(settings);
        setBranding(settings);
      } catch (failure) {
        setProblem(refusal(failure, t, 'branding.couldNotLoad'));
      }
    })();

    void (async () => {
      try {
        const { driverAppTheme } = await settingsApi.appearance();

        setLoadedTheme(driverAppTheme);
        setTheme(driverAppTheme);
      } catch (failure) {
        setProblem(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();
  }, [t]);

  // The confirmation is a moment, not a state: it says the save landed and then gets out of
  // the way, rather than sitting there until the next edit and being read as "this is saved"
  // by somebody who has since changed something.
  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const changeProfile = <K extends keyof FirmProfile>(key: K, value: FirmProfile[K]) => {
    setSaved(false);
    setProfile((current) => (current === null ? current : { ...current, [key]: value }));
  };

  const change = <K extends keyof BrandingSettings>(key: K, value: BrandingSettings[K]) => {
    setSaved(false);
    setBranding((current) => (current === null ? current : { ...current, [key]: value }));
  };

  /**
   * The logo, written to both sides at once.
   *
   * It is already on the server by the time this runs, so counting it as an unsaved change —
   * or letting Discard put the old one back — would both be lies.
   */
  const settleLogo = useCallback((logoUrl: string | null) => {
    setBranding((current) => (current === null ? current : { ...current, logoUrl }));
    setLoadedBranding((current) => (current === null ? current : { ...current, logoUrl }));
  }, []);

  const profileChanged = useMemo(
    () => (loadedProfile === null || profile === null ? 0 : profileChanges(loadedProfile, profile)),
    [loadedProfile, profile],
  );

  /** How many branding fields differ from what was loaded. */
  const brandingChanges = useMemo(() => {
    if (loadedBranding === null || branding === null) return 0;

    return (Object.keys(loadedBranding) as (keyof BrandingSettings)[]).filter(
      (key) => !same(loadedBranding[key], branding[key]),
    ).length;
  }, [loadedBranding, branding]);

  const themeChanged = theme !== null && loadedTheme !== null && theme !== loadedTheme;
  const changes = profileChanged + brandingChanges + (themeChanged ? 1 : 0);

  async function save() {
    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setProblem(invalid);
      return;
    }

    setSaving(true);
    setProblem(null);
    setCleared(null);

    let failure: FieldProblem | null = null;

    if (profile !== null && profileChanged > 0) {
      try {
        // Replaced with what the server stored, not with what was typed: numbers come
        // back normalised and the flag settled, and the form should show the truth.
        // A row added and left empty is not a line: gone from the form before it is sent,
        // so the rows the server numbers are the rows on the screen.
        const tidy = { ...profile, phoneLines: withoutEmptyRows(profile.phoneLines) };

        setProfile(tidy);

        const stored = await profileApi.save(tidy);

        setLoadedProfile(stored);
        setProfile(stored);
      } catch (error) {
        failure = named(refusal(error, t, 'branding.couldNotSaveProfile'), page.current, t);
      }
    }

    if (failure === null && branding !== null && brandingChanges > 0) {
      try {
        // Without the support number: it belongs to the flagged phone line, saved above,
        // and the copy loaded with this form may already be behind it.
        const stored = await brandingApi.save({ ...branding, supportPhone: null });

        setLoadedBranding(stored);
        setBranding(stored);
      } catch (error) {
        failure = named(refusal(error, t, 'branding.couldNotSave'), page.current, t);
      }
    }

    if (failure === null && theme !== null && themeChanged) {
      try {
        const stored = await settingsApi.saveAppearance({ driverAppTheme: theme });

        setLoadedTheme(stored.driverAppTheme);
        setTheme(stored.driverAppTheme);
      } catch (error) {
        failure = named(refusal(error, t, 'settings.couldNotSave'), page.current, t);
      }
    }

    setProblem(failure);

    if (failure === null) {
      setSaved(true);

      // The lines, the words and the legal links are rows on the setup checklist.
      notifySetupChanged();
    }

    setSaving(false);
  }

  async function upload(file: File) {
    setUploading(true);
    setProblem(null);

    try {
      const result = await brandingApi.uploadLogo(file);

      settleLogo(result.logoUrl);

      // The URL never changes, so nothing would make the browser fetch the new bytes.
      setLogoVersion((version) => version + 1);
      notifySetupChanged();
    } catch (error) {
      setProblem(refusal(error, t, 'branding.couldNotUpload'));
    } finally {
      setUploading(false);

      // Cleared so choosing the same file again still fires a change event.
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  async function removeLogo() {
    setProblem(null);

    try {
      await brandingApi.deleteLogo();
      settleLogo(null);
      notifySetupChanged();
    } catch (error) {
      setProblem(refusal(error, t, 'branding.couldNotSave'));
    }
  }

  /**
   * The other, destructive reading of "change the theme for all drivers".
   *
   * Confirmed first, because it cannot be undone: the overrides it clears are individual
   * decisions somebody made about individual people, and there is nowhere to read them back
   * from afterwards. A deliberate action rather than a side effect of saving the default —
   * both readings are wanted, and separating them is what stops a firm wiping every
   * per-driver choice by touching the field above.
   */
  async function applyToAll() {
    setConfirming(false);
    setApplying(true);
    setProblem(null);
    setSaved(false);

    try {
      setCleared((await settingsApi.applyAppearanceToAll()).cleared);
    } catch (error) {
      setProblem(refusal(error, t, 'settings.couldNotSave'));
    } finally {
      setApplying(false);
    }
  }

  return (
    <FieldErrors root={page} problem={problem} onEdit={() => setProblem(null)}>
      <PageHeader
        title={t('nav.settings.branding')}
        description={t('nav.settingsAbout.branding')}
      />

      {problem !== null && <Banner tone="danger">{problem.message}</Banner>}
      {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Section title={t('branding.firmTitle')} description={t('branding.firmIntro')}>
          {profile === null ? (
            <EmptyState>{t('settings.loading')}</EmptyState>
          ) : (
            <SettingRow label={t('branding.displayName')} help={t('branding.displayNameHint')} last>
              <Input
                id="displayName"
                aria-label={t('branding.displayName')}
                value={profile.displayName}
                maxLength={200}
                onChange={(event) => changeProfile('displayName', event.target.value)}
              />
            </SettingRow>
          )}
        </Section>

        <Section title={t('branding.linesTitle')} description={t('branding.linesIntro')}>
          {profile === null ? (
            <EmptyState>{t('settings.loading')}</EmptyState>
          ) : (
            <PhoneLinesEditor
              lines={profile.phoneLines}
              disabled={saving}
              onChange={(phoneLines) => changeProfile('phoneLines', phoneLines)}
            />
          )}
        </Section>

        <Section title={t('branding.aboutTitle')} description={t('branding.aboutIntro')}>
          {profile === null ? (
            <EmptyState>{t('settings.loading')}</EmptyState>
          ) : (
            <>
              <SettingRow label={t('branding.aboutUs')} help={t('branding.aboutUsHint')}>
                <Textarea
                  id="aboutUs"
                  aria-label={t('branding.aboutUs')}
                  rows={4}
                  maxLength={aboutUsMaxLength}
                  value={profile.aboutUs ?? ''}
                  onChange={(event) => changeProfile('aboutUs', event.target.value)}
                />
              </SettingRow>

              <SettingRow label={t('branding.website')} help={t('branding.urlHint')}>
                <Input
                  id="website"
                  type="url"
                  aria-label={t('branding.website')}
                  value={profile.website ?? ''}
                  onChange={(event) => changeProfile('website', event.target.value)}
                />
              </SettingRow>

              <SettingRow label={t('branding.email')} help={t('branding.emailHint')}>
                <Input
                  id="email"
                  type="email"
                  aria-label={t('branding.email')}
                  value={profile.email ?? ''}
                  onChange={(event) => changeProfile('email', event.target.value)}
                />
              </SettingRow>

              <SettingRow label={t('branding.openingHours')} help={t('branding.openingHoursHint')}>
                <Input
                  id="openingHours"
                  aria-label={t('branding.openingHours')}
                  value={profile.openingHours ?? ''}
                  maxLength={120}
                  onChange={(event) => changeProfile('openingHours', event.target.value)}
                />
              </SettingRow>

              <SettingRow label={t('branding.officeAddress')} help={t('branding.officeAddressHint')} last>
                <Input
                  id="officeAddress"
                  aria-label={t('branding.officeAddress')}
                  value={profile.officeAddress ?? ''}
                  maxLength={200}
                  onChange={(event) => changeProfile('officeAddress', event.target.value)}
                />
              </SettingRow>
            </>
          )}
        </Section>

        <Section title={t('branding.title')} description={t('branding.intro')}>
          {branding === null ? (
            <EmptyState>{t('settings.loading')}</EmptyState>
          ) : (
            <>
              <SettingRow
                label={t('branding.primaryColor')}
                help={t('branding.primaryColorHint')}
              >
                {/* A real colour well beside the text, because a firm's accent is picked by
                    eye and typed by whoever was given it on paper. */}
                <input
                  id="primaryColor"
                  type="color"
                  aria-label={t('branding.primaryColor')}
                  value={branding.primaryColor}
                  onChange={(event) => change('primaryColor', event.target.value.toUpperCase())}
                  className="h-8 w-10 shrink-0 cursor-pointer rounded-control border border-rule bg-surface-2 p-1"
                />
                <Input
                  aria-label={t('branding.primaryColor')}
                  value={branding.primaryColor}
                  maxLength={7}
                  onChange={(event) => change('primaryColor', event.target.value.toUpperCase())}
                />
              </SettingRow>

              <SettingRow label={t('branding.termsUrl')} help={t('branding.urlHint')}>
                <Input
                  id="termsUrl"
                  type="url"
                  aria-label={t('branding.termsUrl')}
                  value={branding.termsUrl ?? ''}
                  onChange={(event) => change('termsUrl', event.target.value)}
                />
              </SettingRow>

              <SettingRow label={t('branding.privacyUrl')} help={t('branding.urlHint')}>
                <Input
                  id="privacyUrl"
                  type="url"
                  aria-label={t('branding.privacyUrl')}
                  value={branding.privacyUrl ?? ''}
                  onChange={(event) => change('privacyUrl', event.target.value)}
                />
              </SettingRow>

              <SettingRow
                label={t('branding.logo')}
                help={
                  <>
                    {t('branding.logoHint')}
                    <span className="block mt-2">
                      {branding.logoUrl == null ? (
                        t('branding.noLogo')
                      ) : (
                        <img
                          // The version is what makes a replaced logo actually appear: the
                          // URL is fixed, so without it the browser would show the cached
                          // one until a hard refresh.
                          src={`${branding.logoUrl}?v=${logoVersion}`}
                          alt={t('branding.logo')}
                          className="h-12 w-auto"
                        />
                      )}
                    </span>
                  </>
                }
                last
              >
                {/* Hidden rather than styled: a native file input is a different control on
                    every browser, and none of them is this one. The button is what a person
                    sees and what a screen reader announces. */}
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={t('branding.chooseLogo')}
                  className="hidden"
                  disabled={uploading}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) void upload(file);
                  }}
                />

                <Button
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                >
                  {uploading ? t('branding.uploading') : t('branding.chooseLogo')}
                </Button>

                {branding.logoUrl != null && (
                  <Button size="sm" variant="quiet" onClick={() => void removeLogo()}>
                    {t('branding.removeLogo')}
                  </Button>
                )}
              </SettingRow>
            </>
          )}
        </Section>

        <Section title={t('appearance.title')} description={t('appearance.intro')}>
          {theme === null ? (
            <EmptyState>{t('settings.loading')}</EmptyState>
          ) : (
            <>
              <SettingRow label={t('appearance.driverApp')} help={t('appearance.driverAppHelp')}>
                <Select
                  id="driverAppTheme"
                  aria-label={t('appearance.driverApp')}
                  value={theme}
                  disabled={saving}
                  onChange={(event) => {
                    setSaved(false);
                    setTheme(event.target.value as ThemePreference);
                  }}
                >
                  {THEMES.map((option) => (
                    <option key={option} value={option}>
                      {t(`theme.names.${option.toLowerCase()}`)}
                    </option>
                  ))}
                </Select>
              </SettingRow>

              <SettingRow
                label={t('appearance.applyToAll')}
                help={
                  themeChanged
                    ? `${t('appearance.applyHelp')} ${t('appearance.applyNeedsSave')}`
                    : t('appearance.applyHelp')
                }
                last
              >
                <Button
                  variant="danger"
                  disabled={applying || themeChanged}
                  onClick={() => setConfirming(true)}
                >
                  {applying ? t('settings.saving') : t('appearance.apply')}
                </Button>
              </SettingRow>

              {/* Nobody, and eleven people, deserve different sentences. A screen that said
                  "done" either way would leave an administrator unsure whether it had run. */}
              {cleared !== null && (
                <Banner tone="ok" footer>
                  {cleared === 0
                    ? t('appearance.appliedToNobody')
                    : t('appearance.applied', { count: cleared })}
                </Banner>
              )}
            </>
          )}
        </Section>

        <SaveBar
          changes={changes}
          problem={problem?.message}
          busy={saving}
          discardLabel={t('settings.discard')}
          saveLabel={saving ? t('settings.saving') : t('settings.save')}
          onDiscard={() => {
            setSaved(false);
            setProblem(null);
            setProfile(loadedProfile);
            setBranding(loadedBranding);
            setTheme(loadedTheme);
          }}
          onSave={() => void save()}
        >
          {t('settings.unsaved', { count: changes })}
        </SaveBar>
      </form>

      <Dialog
        open={confirming}
        title={t('appearance.applyToAll')}
        onClose={() => setConfirming(false)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setConfirming(false)}>
              {t('settings.cancel')}
            </Button>
            <Button variant="danger" onClick={() => void applyToAll()}>
              {t('appearance.apply')}
            </Button>
          </>
        }
      >
        <p className="m-0">{t('appearance.applyWarning')}</p>
      </Dialog>
    </FieldErrors>
  );
}
